import { ReactElement, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { FieldArray, Formik } from 'formik'

import { getUnprocessableErrors } from 'utils/error'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'

import styled from 'styled-components'
import { ApolloError } from '@apollo/client'
import { ILoginMFAFormValues } from 'features/Login/types'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import { isEmpty, set } from 'lodash'
import { SpinnerIcon } from 'components/Icons'

const StyledSpinnerIcon = styled(SpinnerIcon)`
  fill: #717171;
`

const CodeInputContainer = styled.div`
  margin-inline: auto;
  display: flex;
  gap: 16px;
  width: 314px;
  height: 43px;
`
const Form = styled.form`
  max-width: 350px;
  width: 100%;
  margin: auto;
`

const DigitInput = styled(Input)<{ hasError?: boolean }>`
  width: 39px;
  height: 100%;
  display: flex;
  & > div {
    padding: 0px !important;
    text-align: center;
    font-size: 16px;
    border: 1px solid ${({ hasError }): string => (hasError ? '#FF627B' : '#EAE9E9')};
    border-radius: 4px;
    &:hover {
      border: 1px solid ${({ hasError }): string => (hasError ? '#FF627B' : '#EAE9E9')};
    }
    &:focus {
      border: 1px solid ${({ hasError }): string => (hasError ? '#FF627B' : '#EAE9E9')};
    }
    & > input {
      text-align: center;
    }
  }
`

const ButtonWrapper = styled.div`
  margin-top: 20px;
`
const CenteredText = styled.p`
  text-align: center;
`

const ErrorMessageWrapper = styled.div`
  margin-bottom: 5px;
`

interface ILoginFormProps {
  error?: ApolloError
  loading: boolean
  reset: () => void
  handleSubmit: (values: ILoginMFAFormValues) => void
  hasCodeSent: boolean
  timerValue: number
  timerLabel: string
  userEmail: string
  handleResendCode: () => void
}

const CODE_LENGTH = 6

const LoginMFAForm = ({
  error,
  loading,
  handleSubmit,
  reset,
  timerValue,
  timerLabel,
  hasCodeSent,
  userEmail,
  handleResendCode
}: ILoginFormProps): ReactElement => {
  const initialValues: ILoginMFAFormValues = {
    code: Array(CODE_LENGTH).fill('') as string[]
  }
  const onSubmit = (values: ILoginMFAFormValues): void => {
    handleSubmit(values)
  }

  const isShowingTimer = hasCodeSent && timerValue > 0
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {(props): ReactElement => {
        const { setFieldValue, values, handleChange, handleBlur, handleSubmit } = props

        const getCurrentIndex = (id: string): number => {
          return parseInt(id.match(/\d+/)?.[0] || '')
        }

        const handleExistingValueChange = (currentIndex: number, value: string, newCode: string[]): void => {
          if (value.split('')[0] !== value.split('')[1]) {
            const oldValue = values.code[currentIndex]
            const incomingValue = value
              .split('')
              .filter((d) => d !== oldValue)
              .join('')

            if (!isNaN(parseInt(incomingValue))) {
              setFieldValue(`code[${currentIndex}]`, incomingValue)
              set(newCode, currentIndex, incomingValue)
            }
          }
        }

        const focusNextInput = (currentIndex: number, value: string): void => {
          if (value && currentIndex < 7) {
            const nextInput = document.querySelector(`input[id="code[${currentIndex + 1}]"]`) as HTMLInputElement
            if (nextInput) {
              nextInput.focus()
            }
          }
        }

        const focusPreviousInput = (currentIndex: number): void => {
          if (currentIndex > 0) {
            const previousInput = document.querySelector(`input[id="code[${currentIndex - 1}]"]`) as HTMLInputElement
            if (previousInput) {
              previousInput.focus()
            }
          }
        }

        const checkCodeCompletion = (code: string[]): void => {
          if (code.every((digit) => digit)) {
            onSubmit({ code: code })
          }
        }

        const handleCopyPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
          e.preventDefault()
          reset() // Reset mutation state and timer
          const newCode = [...values.code]
          const { id } = e.target as HTMLInputElement
          const currentInputIndex = getCurrentIndex(id) // Get the index of the current input in the form

          const pastedValue = e.clipboardData.getData('Text')

          // Process each digit of the pasted value
          for (let i = 0; i < pastedValue.length; i++) {
            const indexToInsert = currentInputIndex + i
            if (indexToInsert < CODE_LENGTH) {
              const digit = pastedValue[i]
              if (!isNaN(parseInt(digit))) {
                setFieldValue(`code[${indexToInsert}]`, digit)
                set(newCode, indexToInsert, digit)
              }
            }
          }

          // Move focus to the next input after the last pasted digit
          const nextIndex = Math.min(currentInputIndex - 1 + pastedValue.length, CODE_LENGTH - 1)
          focusNextInput(nextIndex, pastedValue)
          checkCodeCompletion(newCode)
        }

        const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>): void => {
          if (e.key === 'Backspace') {
            reset() // Reset mutation state and timer
            const { id } = e.target as HTMLInputElement
            const currentInputIndex = getCurrentIndex(id) // Get the index of the current input in the form

            setFieldValue(`code[${currentInputIndex}]`, '')

            focusPreviousInput(currentInputIndex)
          }
        }

        return (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Column>
                <Input readOnly disabled id="email" value={userEmail} onChange={handleChange} />
              </Column>
            </Row>

            <CenteredText>
              <FormattedMessage id="login.mfa.code.message" defaultMessage="Enter the code sent to your email" />
            </CenteredText>

            <CodeInputContainer>
              <FieldArray name="code">
                {(): ReactNode =>
                  props.values.code.map((_, index) => {
                    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
                      reset() //Reset mutation state and timer
                      const newCode = [...values.code]
                      const { value, id = '' } = e.target
                      const currentIndex = getCurrentIndex(id)

                      if (!isEmpty(values.code[currentIndex])) {
                        handleExistingValueChange(currentIndex, value, newCode)
                      } else {
                        setFieldValue(`code[${currentIndex}]`, value)
                        set(newCode, currentIndex, value)
                      }

                      focusNextInput(currentIndex, value)

                      checkCodeCompletion(newCode)
                    }

                    return (
                      <DigitInput
                        onPaste={handleCopyPaste}
                        autoComplete="off"
                        key={index}
                        id={`code[${index}]`}
                        type="text"
                        value={values.code[index]}
                        onKeyDown={handleBackspace}
                        onChange={(e): void => {
                          if (!isNaN(parseInt(e.target.value))) {
                            handleNumberChange(e)
                          }
                        }}
                        onBlur={handleBlur}
                        hasError={!!error}
                      />
                    )
                  })
                }
              </FieldArray>
            </CodeInputContainer>

            <ButtonWrapper>
              {error && (
                <ErrorMessageWrapper>
                  <ErrorMessage errors={getUnprocessableErrors(error)}>
                    <FormattedMessage id="login.mfa.submit.error" defaultMessage="Invalid Code" />
                  </ErrorMessage>
                </ErrorMessageWrapper>
              )}
              {loading && (
                <CenteredText>
                  <StyledSpinnerIcon />
                </CenteredText>
              )}
              {isShowingTimer ? (
                <CenteredText>
                  <p>
                    <FormattedMessage
                      id="login.mfa.code.timer"
                      defaultMessage="Expire time: {value}"
                      values={
                        {
                          value: <strong>{timerLabel}</strong>
                        } as Record<string, ReactNode>
                      }
                    />
                  </p>
                </CenteredText>
              ) : null}
              {hasCodeSent ? (
                <>
                  <CenteredText>
                    <FormattedMessage id="login.mfa.code.resend" defaultMessage="Didn't receive the code?" />
                  </CenteredText>
                  <Button
                    type="button"
                    kind="primary"
                    loading={loading}
                    disabled={isShowingTimer}
                    fullWidth
                    onClick={(e): void => {
                      e.preventDefault()
                      handleResendCode()
                    }}
                  >
                    <FormattedMessage id="dictionary.signin" defaultMessage="Send code again" />
                  </Button>
                </>
              ) : null}
            </ButtonWrapper>
          </Form>
        )
      }}
    </Formik>
  )
}

export default LoginMFAForm
